import get from 'lodash/get';
import { query } from 'utils/graphql';

// Actions
const LOAD = 'voyager/files/LOAD';
const LOAD_SUCCESS = 'voyager/files/LOAD_SUCCESS';
const LOAD_FAILURE = 'voyager/files/LOAD_FAILURE';
const UPLOAD = 'voyager/files/UPLOAD';
const UPLOAD_PROGRESS = 'voyager/files/UPLOAD_PROGRESS';
const UPLOAD_SUCCESS = 'voyager/files/UPLOAD_SUCCESS';
const UPLOAD_FAILURE = 'voyager/files/UPLOAD_FAILURE';
const UPDATE = 'voyager/files/UPDATE';
const UPDATE_SUCCESS = 'voyager/files/UPDATE_SUCCESS';
const UPDATE_FAILURE = 'voyager/files/UPDATE_FAIL';
const DESTROY = 'voyager/files/DESTROY';
const DESTROY_SUCCESS = 'voyager/files/DESTROY_SUCCESS';
const DESTROY_FAILURE = 'voyager/files/DESTROY_FAIL';

// Reducer
const initialState = {
  files: [],
  loading: false,
  uploading: false,
  updating: false,
  destroying: false,
  progress: null,
  error: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD:
      return {
        ...state,
        loading: true,
      };
    case LOAD_SUCCESS:
      return {
        ...state,
        files: action.data.files,
        loading: false,
      };
    case LOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: 'Files could not be loaded',
      };
    case UPLOAD:
      return {
        ...state,
        uploading: true,
        progress: 0,
      };
    case UPLOAD_PROGRESS:
      return {
        ...state,
        progress: action.percent || state.progress,
      };
    case UPLOAD_FAILURE:
      return {
        ...state,
        uploading: false,
        progress: null,
        error: get(action, 'error[0].message', 'File could not be uploaded'),
      };
    case UPLOAD_SUCCESS:
      return {
        ...state,
        uploading: false,
        progress: null,
        files: [
          action.data.file,
          ...state.files,
        ],
      };
    case UPDATE:
      return {
        ...state,
        updating: true,
        error: null,
      };
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        error: get(action, 'error[0].message', 'File could not be updated'),
      };
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        files: state.files.map((file) => {
          if (file.id !== action.id) {
            return file;
          }

          return action.data.file;
        }),
      };
    case DESTROY:
      return {
        ...state,
        destroying: true,
      };
    case DESTROY_SUCCESS:
      return {
        ...state,
        destroying: false,
        files: state.files.filter(({ id }) => id !== action.id),
      };
    case DESTROY_FAILURE:
      return {
        ...state,
        destroying: false,
        error: 'File could not be deleted',
      };
    default:
      return state;
  }
}

// Selectors
export const getAll = state => (
  state.files.files
);

export const getError = state => (
  state.files.error
);

export const isLoading = state => (
  state.files.loading
);

export const isUploading = state => (
  state.files.uploading
);

export const isUpdating = state => (
  state.files.updating
);

export const isDestroying = state => (
  state.files.destroying
);

export const getProgress = state => (
  state.files.progress
);

// Action Creators
export function load() {
  return {
    types: [LOAD, LOAD_SUCCESS, LOAD_FAILURE],
    promise: query`
      query GetFiles {
        files {
          id
          filename
          path
          name
        }
      }
    `,
  };
}

function progress(percent) {
  return {
    type: UPLOAD_PROGRESS,
    percent,
  };
}

export function upload(file, details) {
  // Stuff the file into form data to transmit it
  const data = new FormData();
  data.append('asset', file);
  const queryString = 'mutation+UploadFile($details:FileInput!){file:uploadFile(details:$details)' +
    '{id,filename,path,name}}';
  const variableString = encodeURIComponent(JSON.stringify({ details }, null, 0));

  return {
    types: [UPLOAD, UPLOAD_SUCCESS, UPLOAD_FAILURE],
    force: true,
    details,
    promise: (client, { dispatch }) => client.post(`/graphql?query=${queryString}&variables=${variableString}`, {
      data,
      modify: (request) => {
        // When progress is reported back to the browser, fire an event
        request.on('progress', ({ percent }) => {
          dispatch(progress(percent));
        });
      },
    }),
  };
}

export function update(id, details) {
  return {
    types: [UPDATE, UPDATE_SUCCESS, UPDATE_FAILURE],
    id,
    promise: query`
      mutation UpdateFile(${{ id }}: String!, ${{ details }}: FileInput!) {
        file: updateFile(id: $id, details: $details) {
          id
          filename
          path
          name
        }
      }
    `,
  };
}

export function destroy(id) {
  return {
    types: [DESTROY, DESTROY_SUCCESS, DESTROY_FAILURE],
    id,
    promise: query`
      mutation DeleteFile(${{ id }}: String!) {
        deleteFile(id: $id)
      }
    `,
  };
}
